import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ReactECharts from 'echarts-for-react';

const {
  FiHome, FiTrendingUp, FiDollarSign, FiPercent, FiCalendar, FiX
} = FiIcons;

function RentalYieldCalculator({ onClose }) {
  const [formData, setFormData] = useState({
    propertyValue: 10000000,
    annualAppreciation: 8,
    monthlyRent: 34000,
    monthlyMaintenance: 4000,
    annualIncrement: 5,
    sipAmount: 30000,
    sipReturn: 12,
    investmentPeriod: 15,
    registrationCost: 600000,
    lumpsumInvestment: 0,
    lumpsumReturn: 12,
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: parseFloat(value) || 0 }));
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(value);
  };

  const calculations = useMemo(() => {
    const years = formData.investmentPeriod;
    
    // Property Future Value
    const propertyFutureValue = formData.propertyValue * Math.pow(1 + formData.annualAppreciation / 100, years);
    
    // SIP Future Value (Rent-Minus invested as SIP)
    const monthlyReturn = formData.sipReturn / 100 / 12;
    const totalMonths = years * 12;
    let sipFutureValue = 0;
    
    if (monthlyReturn > 0) {
      sipFutureValue = formData.sipAmount * (Math.pow(1 + monthlyReturn, totalMonths) - 1) / monthlyReturn;
    } else {
      sipFutureValue = formData.sipAmount * totalMonths;
    }

    // Lumpsum Investment Future Value
    const lumpsumFutureValue = formData.lumpsumInvestment * Math.pow(1 + formData.lumpsumReturn / 100, years);
    
    // Total Investment in SIP
    const totalSIPInvestment = formData.sipAmount * 12 * years;
    
    // Rental Income Calculation (year by year) - FOR REFERENCE ONLY, NOT ADDED TO TOTAL
    let totalRentalIncome = 0;
    let totalMaintenanceCost = 0;
    let currentRent = formData.monthlyRent;
    let currentMaintenance = formData.monthlyMaintenance;
    const yearlyBreakdown = [];
    
    for (let year = 1; year <= years; year++) {
      const yearlyRent = currentRent * 12;
      const yearlyMaintenance = currentMaintenance * 12;
      const netRental = yearlyRent - yearlyMaintenance;
      
      totalRentalIncome += yearlyRent;
      totalMaintenanceCost += yearlyMaintenance;
      
      yearlyBreakdown.push({
        year,
        rent: yearlyRent,
        maintenance: yearlyMaintenance,
        netRental,
        cumulativeNet: totalRentalIncome - totalMaintenanceCost
      });
      
      // Increment for next year
      currentRent = currentRent * (1 + formData.annualIncrement / 100);
      currentMaintenance = currentMaintenance * (1 + formData.annualIncrement / 100);
    }
    
    const netRentalIncome = totalRentalIncome - totalMaintenanceCost;
    
    // CRITICAL FIX: Total Value (Property Investment Route)
    // Property + SIP + Lumpsum ONLY (Net Rental NOT included as it's invested as SIP)
    const totalValuePropertyRoute = propertyFutureValue + sipFutureValue + lumpsumFutureValue;
    
    // Alternative: If entire property investment was invested as lumpsum
    const totalPropertyInvestment = formData.propertyValue + formData.registrationCost;
    const alternativeLumpsumFutureValue = totalPropertyInvestment * Math.pow(1 + formData.lumpsumReturn / 100, years);
    
    // Alternative total value (Lumpsum route ONLY, no SIP)
    const totalValueLumpsumRoute = alternativeLumpsumFutureValue + lumpsumFutureValue;
    
    // Capital Gains Calculation - Property Route
    const propertyProfit = propertyFutureValue - formData.propertyValue;
    const taxableGains = Math.max(0, propertyProfit - 125000);
    const ltcgTax = taxableGains * 0.125;
    const postTaxPropertyValue = propertyFutureValue - ltcgTax;
    
    // Total Value After Tax (Property Route)
    const totalValueAfterTaxPropertyRoute = postTaxPropertyValue + sipFutureValue + lumpsumFutureValue;
    
    // Capital Gains Calculation - Lumpsum Route
    const lumpsumProfit = alternativeLumpsumFutureValue - totalPropertyInvestment;
    const lumpsumTaxableGains = Math.max(0, lumpsumProfit - 125000);
    const lumpsumLtcgTax = lumpsumTaxableGains * 0.125;
    const postTaxLumpsumValue = alternativeLumpsumFutureValue - lumpsumLtcgTax;
    
    // Total Value After Tax (Lumpsum Route) - NO SIP
    const totalValueAfterTaxLumpsumRoute = postTaxLumpsumValue + lumpsumFutureValue;

    // Current Rental Yield
    const currentRentalYield = ((formData.monthlyRent - formData.monthlyMaintenance) * 12 / formData.propertyValue) * 100;
    
    // Average Rental Yield over period
    const avgRentalYield = (netRentalIncome / years / formData.propertyValue) * 100;
    
    // Total Initial Investment
    const totalInitialInvestment = formData.propertyValue + formData.registrationCost + formData.lumpsumInvestment;
    
    // Rental Yield Calculation (for reference)
    const totalPropertyPlusSIPValue = propertyFutureValue + sipFutureValue + lumpsumFutureValue;
    const rentalYield = totalPropertyPlusSIPValue > 0 
      ? (netRentalIncome / totalPropertyPlusSIPValue) * 100 
      : 0;

    return {
      // Property Route
      propertyFutureValue,
      sipFutureValue,
      lumpsumFutureValue,
      totalSIPInvestment,
      totalRentalIncome,
      totalMaintenanceCost,
      netRentalIncome,
      totalValuePropertyRoute,
      propertyProfit,
      taxableGains,
      ltcgTax,
      postTaxPropertyValue,
      totalValueAfterTaxPropertyRoute,
      currentRentalYield,
      avgRentalYield,
      yearlyBreakdown,
      sipReturns: sipFutureValue - totalSIPInvestment,
      lumpsumReturns: lumpsumFutureValue - formData.lumpsumInvestment,
      totalInitialInvestment,
      rentalYield,
      
      // Lumpsum Alternative Route
      totalPropertyInvestment,
      alternativeLumpsumFutureValue,
      totalValueLumpsumRoute,
      lumpsumProfit,
      lumpsumTaxableGains,
      lumpsumLtcgTax,
      postTaxLumpsumValue,
      totalValueAfterTaxLumpsumRoute,
      
      // Comparison
      valueDifference: totalValueAfterTaxPropertyRoute - totalValueAfterTaxLumpsumRoute,
      percentageDifference: totalValueAfterTaxLumpsumRoute > 0 
        ? ((totalValueAfterTaxPropertyRoute - totalValueAfterTaxLumpsumRoute) / totalValueAfterTaxLumpsumRoute) * 100 
        : 0,
    };
  }, [formData]);

  // Chart for rental income over time
  const rentalChartOption = {
    tooltip: {
      trigger: 'axis',
      formatter: function(params) {
        const year = params[0].axisValue;
        return `Year ${year}<br/>
                Rental Income: ${formatCurrency(params[0].value)}<br/>
                Net Rental: ${formatCurrency(params[1].value)}`;
      }
    },
    legend: {
      data: ['Rental Income', 'Net Rental'],
      top: 25
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: calculations.yearlyBreakdown.map(item => item.year),
      name: 'Year'
    },
    yAxis: {
      type: 'value',
      name: 'Amount (₹)',
      axisLabel: {
        formatter: (value) => `₹${(value / 100000).toFixed(0)}L`
      }
    },
    series: [
      {
        name: 'Rental Income',
        type: 'bar',
        data: calculations.yearlyBreakdown.map(item => item.rent),
        itemStyle: { color: '#10b981' }
      },
      {
        name: 'Net Rental',
        type: 'line',
        data: calculations.yearlyBreakdown.map(item => item.netRental),
        itemStyle: { color: '#19b48d' },
        lineStyle: { width: 2 }
      }
    ]
  };

  // Comparison chart
  const comparisonChartOption = {
    tooltip: {
      trigger: 'item',
      formatter: '{b}: {c} ({d}%)'
    },
    legend: {
      orient: 'vertical',
      left: 'left',
      data: ['Property Route (After Tax)', 'Lumpsum Route (After Tax)']
    },
    series: [
      {
        name: 'Value Comparison',
        type: 'pie',
        radius: ['50%', '70%'],
        avoidLabelOverlap: false,
        label: {
          show: true,
          formatter: '{b}: {c}'
        },
        emphasis: {
          label: {
            show: true,
            fontSize: '16',
            fontWeight: 'bold'
          }
        },
        data: [
          { 
            value: calculations.totalValueAfterTaxPropertyRoute, 
            name: 'Property Route (After Tax)',
            itemStyle: { color: '#3b82f6' }
          },
          { 
            value: calculations.totalValueAfterTaxLumpsumRoute, 
            name: 'Lumpsum Route (After Tax)',
            itemStyle: { color: '#f59e0b' }
          }
        ]
      }
    ]
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-xl shadow-xl w-full max-w-7xl max-h-[95vh] overflow-y-auto my-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-lg bg-[#19b48d] flex items-center justify-center">
              <SafeIcon icon={FiHome} className="text-white text-sm" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              Rental Yield Calculator
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
          >
            <SafeIcon icon={FiX} className="text-lg" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            
            {/* Input Section */}
            <div className="lg:col-span-1 space-y-6">
              
              {/* Property Details */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h4 className="text-md font-semibold text-gray-900 mb-4">Property Details</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Property Current Market Value ₹
                    </label>
                    <input
                      type="number"
                      value={formData.propertyValue}
                      onChange={(e) => handleInputChange('propertyValue', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19b48d] focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">{formatCurrency(formData.propertyValue)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Registration & Stamp Duty Cost ₹
                    </label>
                    <input
                      type="number"
                      value={formData.registrationCost}
                      onChange={(e) => handleInputChange('registrationCost', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19b48d] focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">{formatCurrency(formData.registrationCost)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Expected Annual Appreciation (%)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={formData.annualAppreciation}
                      onChange={(e) => handleInputChange('annualAppreciation', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19b48d] focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Rental Details */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h4 className="text-md font-semibold text-gray-900 mb-4">Rental Details</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Current Monthly Rent ₹
                    </label>
                    <input
                      type="number"
                      value={formData.monthlyRent}
                      onChange={(e) => handleInputChange('monthlyRent', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19b48d] focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">{formatCurrency(formData.monthlyRent)}/month</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Monthly Property Maintenance Cost ₹
                    </label>
                    <input
                      type="number"
                      value={formData.monthlyMaintenance}
                      onChange={(e) => handleInputChange('monthlyMaintenance', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19b48d] focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">{formatCurrency(formData.monthlyMaintenance)}/month</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Expected Annual Increment (Rent & Expenses) (%)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={formData.annualIncrement}
                      onChange={(e) => handleInputChange('annualIncrement', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19b48d] focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Investment Details */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h4 className="text-md font-semibold text-gray-900 mb-4">Investment Details</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Lumpsum Investment ₹
                    </label>
                    <input
                      type="number"
                      value={formData.lumpsumInvestment}
                      onChange={(e) => handleInputChange('lumpsumInvestment', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19b48d] focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">{formatCurrency(formData.lumpsumInvestment)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Lumpsum Expected Return (%)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={formData.lumpsumReturn}
                      onChange={(e) => handleInputChange('lumpsumReturn', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19b48d] focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Rent-Minus as SIP ₹
                    </label>
                    <input
                      type="number"
                      value={formData.sipAmount}
                      onChange={(e) => handleInputChange('sipAmount', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19b48d] focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">{formatCurrency(formData.sipAmount)}/month</p>
                    <p className="text-xs text-blue-600 mt-1">
                      Net rental income invested as SIP in mutual funds
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      SIP Assumed Rate of Return (%)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={formData.sipReturn}
                      onChange={(e) => handleInputChange('sipReturn', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19b48d] focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Investment Period (Years)
                    </label>
                    <input
                      type="number"
                      value={formData.investmentPeriod}
                      onChange={(e) => handleInputChange('investmentPeriod', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19b48d] focus:border-transparent"
                      min="1" max="50"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Results Section */}
            <div className="lg:col-span-2 space-y-6">
              
              {/* Key Comparison */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                
                {/* Property Investment Route */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl shadow-sm border border-blue-200 p-6">
                  <h4 className="text-md font-semibold text-blue-900 mb-4">Property Investment Route</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-blue-700">Property Value:</span>
                      <span className="text-lg font-bold text-blue-900">{formatCurrency(calculations.propertyFutureValue)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-blue-700">SIP Value (Rental Invested):</span>
                      <span className="text-lg font-bold text-blue-900">{formatCurrency(calculations.sipFutureValue)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-blue-700">Lumpsum Value:</span>
                      <span className="text-lg font-bold text-blue-900">{formatCurrency(calculations.lumpsumFutureValue)}</span>
                    </div>
                    <div className="flex justify-between items-center border-t border-blue-300 pt-3">
                      <span className="text-sm text-blue-700">LTCG Tax:</span>
                      <span className="text-lg font-bold text-red-700">-{formatCurrency(calculations.ltcgTax)}</span>
                    </div>
                    <div className="flex justify-between items-center pt-3 border-t border-blue-300">
                      <span className="text-sm font-medium text-blue-800">Total After Tax:</span>
                      <span className="text-xl font-bold text-blue-900">{formatCurrency(calculations.totalValueAfterTaxPropertyRoute)}</span>
                    </div>
                  </div>
                </div>

                {/* Lumpsum Alternative Route */}
                <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl shadow-sm border border-orange-200 p-6">
                  <h4 className="text-md font-semibold text-orange-900 mb-4">Lumpsum Alternative Route</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-orange-700">Property Investment:</span>
                      <span className="text-lg font-bold text-orange-900">{formatCurrency(calculations.totalPropertyInvestment)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-orange-700">Lumpsum Future Value:</span>
                      <span className="text-lg font-bold text-orange-900">{formatCurrency(calculations.alternativeLumpsumFutureValue)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-orange-700">Additional Lumpsum:</span>
                      <span className="text-lg font-bold text-orange-900">{formatCurrency(calculations.lumpsumFutureValue)}</span>
                    </div>
                    <div className="flex justify-between items-center border-t border-orange-300 pt-3">
                      <span className="text-sm text-orange-700">LTCG Tax:</span>
                      <span className="text-lg font-bold text-red-700">-{formatCurrency(calculations.lumpsumLtcgTax)}</span>
                    </div>
                    <div className="flex justify-between items-center pt-3 border-t border-orange-300">
                      <span className="text-sm font-medium text-orange-800">Total After Tax:</span>
                      <span className="text-xl font-bold text-orange-900">{formatCurrency(calculations.totalValueAfterTaxLumpsumRoute)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Comparison Analysis */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl shadow-sm border border-green-200 p-6">
                <h4 className="text-md font-semibold text-green-900 mb-4">Comparison Analysis</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-sm text-green-700 mb-1">Better Option</p>
                    <p className="text-2xl font-bold text-green-900">
                      {calculations.valueDifference > 0 ? 'Property Route' : 'Lumpsum Route'}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-green-700 mb-1">Value Difference</p>
                    <p className="text-2xl font-bold text-green-900">{formatCurrency(Math.abs(calculations.valueDifference))}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-green-700 mb-1">Percentage Difference</p>
                    <p className="text-2xl font-bold text-green-900">{Math.abs(calculations.percentageDifference).toFixed(2)}%</p>
                  </div>
                </div>
              </div>

              {/* Rental Yield Reference */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h4 className="text-md font-semibold text-gray-900 mb-4">Rental Income Reference (Not Added to Total)</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <SafeIcon icon={FiPercent} className="text-blue-600 text-2xl mx-auto mb-2" />
                    <p className="text-sm text-blue-700 mb-1">Current Rental Yield</p>
                    <p className="text-2xl font-bold text-blue-900">{calculations.currentRentalYield.toFixed(2)}%</p>
                    <p className="text-xs text-blue-600 mt-1">Annual net rent / Property value</p>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <SafeIcon icon={FiTrendingUp} className="text-green-600 text-2xl mx-auto mb-2" />
                    <p className="text-sm text-green-700 mb-1">Total Net Rental</p>
                    <p className="text-2xl font-bold text-green-900">{formatCurrency(calculations.netRentalIncome)}</p>
                    <p className="text-xs text-green-600 mt-1">Over {formData.investmentPeriod} years</p>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <SafeIcon icon={FiDollarSign} className="text-purple-600 text-2xl mx-auto mb-2" />
                    <p className="text-sm text-purple-700 mb-1">Invested as SIP</p>
                    <p className="text-2xl font-bold text-purple-900">{formatCurrency(calculations.sipFutureValue)}</p>
                    <p className="text-xs text-purple-600 mt-1">Rental invested in mutual funds</p>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                  <p className="text-sm text-yellow-800">
                    <strong>Note:</strong> Net rental income is invested as SIP in mutual funds (₹{formatCurrency(formData.sipAmount)}/month) and is NOT added directly to the property route total. The SIP future value is included in the total.
                  </p>
                </div>
              </div>

              {/* Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h4 className="text-md font-semibold text-gray-900 mb-4">Rental Income Over Time</h4>
                  <div className="h-80">
                    <ReactECharts option={rentalChartOption} style={{ height: '100%', width: '100%' }} />
                  </div>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h4 className="text-md font-semibold text-gray-900 mb-4">Route Comparison (After Tax)</h4>
                  <div className="h-80">
                    <ReactECharts option={comparisonChartOption} style={{ height: '100%', width: '100%' }} />
                  </div>
                </div>
              </div>

              {/* Summary Table */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h4 className="text-md font-semibold text-gray-900 mb-4">Complete Investment Summary</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Component</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-700">Property Route</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-700">Lumpsum Route</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-700">Difference</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-gray-100">
                        <td className="py-3 px-4 text-gray-900 font-medium">Initial Investment</td>
                        <td className="py-3 px-4 text-right text-gray-900">{formatCurrency(calculations.totalInitialInvestment)}</td>
                        <td className="py-3 px-4 text-right text-gray-900">{formatCurrency(calculations.totalPropertyInvestment)}</td>
                        <td className="py-3 px-4 text-right text-gray-900">{formatCurrency(calculations.totalInitialInvestment - calculations.totalPropertyInvestment)}</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-3 px-4 text-gray-900 font-medium">Property/Lumpsum Value</td>
                        <td className="py-3 px-4 text-right text-blue-700 font-semibold">{formatCurrency(calculations.propertyFutureValue)}</td>
                        <td className="py-3 px-4 text-right text-orange-700 font-semibold">{formatCurrency(calculations.alternativeLumpsumFutureValue)}</td>
                        <td className="py-3 px-4 text-right text-green-700 font-semibold">{formatCurrency(calculations.propertyFutureValue - calculations.alternativeLumpsumFutureValue)}</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-3 px-4 text-gray-900 font-medium">SIP Value (Rental Invested)</td>
                        <td className="py-3 px-4 text-right text-blue-700 font-semibold">{formatCurrency(calculations.sipFutureValue)}</td>
                        <td className="py-3 px-4 text-right text-orange-700 font-semibold">₹0</td>
                        <td className="py-3 px-4 text-right text-green-700 font-semibold">{formatCurrency(calculations.sipFutureValue)}</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-3 px-4 text-gray-900 font-medium">Additional Lumpsum Value</td>
                        <td className="py-3 px-4 text-right text-blue-700 font-semibold">{formatCurrency(calculations.lumpsumFutureValue)}</td>
                        <td className="py-3 px-4 text-right text-orange-700 font-semibold">{formatCurrency(calculations.lumpsumFutureValue)}</td>
                        <td className="py-3 px-4 text-right text-gray-900">₹0</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-3 px-4 text-gray-900 font-medium">LTCG Tax</td>
                        <td className="py-3 px-4 text-right text-red-600 font-semibold">-{formatCurrency(calculations.ltcgTax)}</td>
                        <td className="py-3 px-4 text-right text-red-600 font-semibold">-{formatCurrency(calculations.lumpsumLtcgTax)}</td>
                        <td className="py-3 px-4 text-right text-red-600 font-semibold">-{formatCurrency(calculations.ltcgTax - calculations.lumpsumLtcgTax)}</td>
                      </tr>
                      <tr className="bg-blue-50">
                        <td className="py-3 px-4 text-gray-900 font-bold">Total Value (After Tax)</td>
                        <td className="py-3 px-4 text-right text-blue-700 font-bold text-lg">{formatCurrency(calculations.totalValueAfterTaxPropertyRoute)}</td>
                        <td className="py-3 px-4 text-right text-orange-700 font-bold text-lg">{formatCurrency(calculations.totalValueAfterTaxLumpsumRoute)}</td>
                        <td className="py-3 px-4 text-right text-green-700 font-bold text-lg">{formatCurrency(calculations.valueDifference)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Key Insights */}
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-200 p-6">
                <h4 className="text-md font-semibold text-gray-900 mb-4">Key Insights</h4>
                <div className="space-y-3">
                  <div className="flex items-start space-x-2">
                    <span className="text-indigo-600 mt-1">•</span>
                    <p className="text-sm text-gray-700">
                      <strong>Property Investment Route:</strong> Your total wealth after {formData.investmentPeriod} years will be <strong>{formatCurrency(calculations.totalValueAfterTaxPropertyRoute)}</strong> (after LTCG tax).
                    </p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <span className="text-indigo-600 mt-1">•</span>
                    <p className="text-sm text-gray-700">
                      <strong>Rental Investment Strategy:</strong> Net rental income of {formatCurrency(calculations.netRentalIncome)} over {formData.investmentPeriod} years is invested as SIP in mutual funds, growing to {formatCurrency(calculations.sipFutureValue)}.
                    </p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <span className="text-indigo-600 mt-1">•</span>
                    <p className="text-sm text-gray-700">
                      <strong>Lumpsum Alternative Route:</strong> If you invested the property amount ({formatCurrency(calculations.totalPropertyInvestment)}) as lumpsum at {formData.lumpsumReturn}% return, plus the additional lumpsum, your wealth would be <strong>{formatCurrency(calculations.totalValueAfterTaxLumpsumRoute)}</strong>.
                    </p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <span className="text-indigo-600 mt-1">•</span>
                    <p className="text-sm text-gray-700">
                      <strong>Better Option:</strong> The <strong>{calculations.valueDifference > 0 ? 'Property Investment Route' : 'Lumpsum Alternative Route'}</strong> gives you <strong>{formatCurrency(Math.abs(calculations.valueDifference))}</strong> more ({Math.abs(calculations.percentageDifference).toFixed(2)}% higher).
                    </p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <span className="text-indigo-600 mt-1">•</span>
                    <p className="text-sm text-gray-700">
                      <strong>Current Rental Yield:</strong> Your property generates <strong>{calculations.currentRentalYield.toFixed(2)}%</strong> annual net rental yield.
                    </p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <span className="text-indigo-600 mt-1">•</span>
                    <p className="text-sm text-gray-700">
                      <strong>Tax Impact:</strong> Property route incurs LTCG tax of {formatCurrency(calculations.ltcgTax)} vs Lumpsum route's {formatCurrency(calculations.lumpsumLtcgTax)} (both at 12.5% on gains above ₹1.25L exemption).
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default RentalYieldCalculator;